import React, { createContext, useContext, useState, useCallback } from 'react';

// ============================================
// COMPETITION CONTEXT
// Gestione centralizzata dello stato delle gare
// ============================================

const CompetitionContext = createContext(null);

// Chiavi localStorage per persistenza
const STORAGE_KEYS = {
    TEAMS: 'teams',
    MATCHES: 'matches',
    BRACKET: 'bracket',
    COMPETITION_TYPE: 'competitionType',
    STOCCATE_PER_ASSALTO: 'stoccatePerAssalto',
    STOCCATE_ELIMINAZIONE: 'stoccateEliminazione',
    NUM_POOLS: 'numPools',
    QUALIFIED_ATHLETES: 'qualified_athletes',
};

// Funzioni helper per localStorage
const loadFromStorage = (key, defaultValue = null) => {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultValue;
    } catch {
        return defaultValue;
    }
};

const saveToStorage = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.error(`Errore salvataggio ${key}: `, error);
    }
};

// Provider Component
export const CompetitionProvider = ({ children }) => {
    // ========== STATO PRINCIPALE ==========

    // Teams/Atleti
    const [teams, setTeamsState] = useState(() => loadFromStorage(STORAGE_KEYS.TEAMS, []));

    // Matches/Incontri
    const [matches, setMatchesState] = useState(() => loadFromStorage(STORAGE_KEYS.MATCHES, []));

    // Bracket eliminazione
    const [bracket, setBracketState] = useState(() => loadFromStorage(STORAGE_KEYS.BRACKET, null));

    // Tipo competizione
    const [competitionType, setCompetitionTypeState] = useState(
        () => localStorage.getItem(STORAGE_KEYS.COMPETITION_TYPE) || 'team'
    );

    // Impostazioni
    const [settings, setSettings] = useState(() => ({
        stoccatePerAssalto: parseInt(localStorage.getItem(STORAGE_KEYS.STOCCATE_PER_ASSALTO)) || 5,
        stoccateEliminazione: parseInt(localStorage.getItem(STORAGE_KEYS.STOCCATE_ELIMINAZIONE)) || 15,
        numPools: parseInt(localStorage.getItem(STORAGE_KEYS.NUM_POOLS)) || 1,
    }));

    // ========== AZIONI TEAMS ==========

    const setTeams = useCallback((newTeams) => {
        const teamsArray = Array.isArray(newTeams) ? newTeams : [];
        setTeamsState(teamsArray);
        saveToStorage(STORAGE_KEYS.TEAMS, teamsArray);
    }, []);

    const addTeam = useCallback((team) => {
        setTeamsState(prev => {
            const updated = [...prev, team];
            saveToStorage(STORAGE_KEYS.TEAMS, updated);
            return updated;
        });
    }, []);

    const updateTeam = useCallback((index, updates) => {
        setTeamsState(prev => {
            const updated = prev.map((team, i) =>
                i === index ? { ...team, ...updates } : team
            );
            saveToStorage(STORAGE_KEYS.TEAMS, updated);
            return updated;
        });
    }, []);

    const removeTeam = useCallback((index) => {
        setTeamsState(prev => {
            const updated = prev.filter((_, i) => i !== index);
            saveToStorage(STORAGE_KEYS.TEAMS, updated);
            return updated;
        });
    }, []);

    // ========== AZIONI MATCHES ==========

    const setMatches = useCallback((newMatches) => {
        if (!Array.isArray(newMatches)) {
            console.error('[CompetitionContext] setMatches: newMatches non è un array!');
            return;
        }
        setMatchesState(newMatches);
        saveToStorage(STORAGE_KEYS.MATCHES, newMatches);
    }, []);

    const updateMatch = useCallback((matchId, updates) => {
        setMatchesState(prev => {
            const updated = prev.map((match, i) => {
                // Supporta sia ID numerico che indice
                const isMatch = match.id === matchId || i === matchId;
                return isMatch ? { ...match, ...updates } : match;
            });
            saveToStorage(STORAGE_KEYS.MATCHES, updated);
            return updated;
        });
    }, []);

    const completeMatch = useCallback((matchId, scoreA, scoreB, additionalData = {}) => {
        setMatchesState(prev => {
            const updated = prev.map((match, i) => {
                const isMatch = match.id === matchId || i === matchId;
                if (isMatch) {
                    return {
                        ...match,
                        scoreA,
                        scoreB,
                        completed: true,
                        ...additionalData
                    };
                }
                return match;
            });
            saveToStorage(STORAGE_KEYS.MATCHES, updated);
            return updated;
        });
    }, []);

    // ========== AZIONI BRACKET ==========

    const setBracket = useCallback((newBracket) => {
        setBracketState(newBracket);
        saveToStorage(STORAGE_KEYS.BRACKET, newBracket);
    }, []);

    const updateBracketMatch = useCallback((roundIndex, matchIndex, updates) => {
        setBracketState(prev => {
            if (!prev || !prev[roundIndex] || !prev[roundIndex][matchIndex]) {
                return prev;
            }
            const updated = prev.map((round, rIdx) =>
                rIdx === roundIndex
                    ? round.map((match, mIdx) =>
                        mIdx === matchIndex ? { ...match, ...updates } : match
                    )
                    : round
            );
            saveToStorage(STORAGE_KEYS.BRACKET, updated);
            return updated;
        });
    }, []);

    // ========== AZIONI COMPETITION TYPE ==========

    const setCompetitionType = useCallback((type) => {
        setCompetitionTypeState(type);
        localStorage.setItem(STORAGE_KEYS.COMPETITION_TYPE, type);
    }, []);

    // ========== AZIONI SETTINGS ==========

    const updateSettings = useCallback((updates) => {
        setSettings(prev => {
            const updated = { ...prev, ...updates };

            // Salva ogni impostazione nel localStorage
            if (updates.stoccatePerAssalto !== undefined) {
                localStorage.setItem(STORAGE_KEYS.STOCCATE_PER_ASSALTO, updates.stoccatePerAssalto);
            }
            if (updates.stoccateEliminazione !== undefined) {
                localStorage.setItem(STORAGE_KEYS.STOCCATE_ELIMINAZIONE, updates.stoccateEliminazione);
            }
            if (updates.numPools !== undefined) {
                localStorage.setItem(STORAGE_KEYS.NUM_POOLS, updates.numPools);
            }

            return updated;
        });
    }, []);

    // ========== AZIONI GLOBALI ==========

    const resetAll = useCallback(() => {
        // Reset stato
        setTeamsState([]);
        setMatchesState([]);
        setBracketState(null);
        setCompetitionTypeState('team');
        setSettings({
            stoccatePerAssalto: 5,
            stoccateEliminazione: 15,
            numPools: 1,
        });

        // Pulisci localStorage
        Object.values(STORAGE_KEYS).forEach(key => {
            localStorage.removeItem(key);
        });

        // Pulisci anche altre chiavi usate dal progetto
        localStorage.removeItem('qualified_athletes');
        localStorage.removeItem('byeAdvancementNeeded');
        localStorage.removeItem('invertiLati');
    }, []);

    const reloadFromStorage = useCallback(() => {
        setTeamsState(loadFromStorage(STORAGE_KEYS.TEAMS, []));
        setMatchesState(loadFromStorage(STORAGE_KEYS.MATCHES, []));
        setBracketState(loadFromStorage(STORAGE_KEYS.BRACKET, null));
        setCompetitionTypeState(localStorage.getItem(STORAGE_KEYS.COMPETITION_TYPE) || 'team');
        setSettings({
            stoccatePerAssalto: parseInt(localStorage.getItem(STORAGE_KEYS.STOCCATE_PER_ASSALTO)) || 5,
            stoccateEliminazione: parseInt(localStorage.getItem(STORAGE_KEYS.STOCCATE_ELIMINAZIONE)) || 15,
            numPools: parseInt(localStorage.getItem(STORAGE_KEYS.NUM_POOLS)) || 1,
        });
    }, []);

    // ========== COMPUTED VALUES ==========

    const completedMatchesCount = matches.filter(m => m.completed).length;
    const totalMatchesCount = matches.length;
    const allMatchesCompleted = totalMatchesCount > 0 && completedMatchesCount === totalMatchesCount;

    // ========== CONTEXT VALUE ==========

    const value = {
        // Stato
        teams,
        matches,
        bracket,
        competitionType,
        settings,

        // Computed
        completedMatchesCount,
        totalMatchesCount,
        allMatchesCompleted,

        // Azioni Teams
        setTeams,
        addTeam,
        updateTeam,
        removeTeam,

        // Azioni Matches
        setMatches,
        updateMatch,
        completeMatch,

        // Azioni Bracket
        setBracket,
        updateBracketMatch,

        // Azioni Competition Type
        setCompetitionType,

        // Azioni Settings
        updateSettings,

        // Azioni Globali
        resetAll,
        reloadFromStorage,

        // Costanti
        STORAGE_KEYS,
    };

    return (
        <CompetitionContext.Provider value={value}>
            {children}
        </CompetitionContext.Provider>
    );
};

// ========== HOOK CUSTOM ==========

export const useCompetition = () => {
    const context = useContext(CompetitionContext);
    if (!context) {
        throw new Error('useCompetition deve essere usato all\'interno di CompetitionProvider');
    }
    return context;
};

// Hook per solo lettura (performance)
export const useCompetitionState = () => {
    const { teams, matches, bracket, competitionType, settings } = useCompetition();
    return { teams, matches, bracket, competitionType, settings };
};

// Hook per solo le azioni (performance)
export const useCompetitionActions = () => {
    const {
        setTeams, addTeam, updateTeam, removeTeam,
        setMatches, updateMatch, completeMatch,
        setBracket, updateBracketMatch,
        setCompetitionType,
        updateSettings,
        resetAll, reloadFromStorage
    } = useCompetition();

    return {
        setTeams, addTeam, updateTeam, removeTeam,
        setMatches, updateMatch, completeMatch,
        setBracket, updateBracketMatch,
        setCompetitionType,
        updateSettings,
        resetAll, reloadFromStorage
    };
};

export default CompetitionContext;
